import type { ReactNode } from "react";
import "./responsibility-guide.css";

const RESPONSIBILITIES = [
  {
    id: "creator",
    name: "创作者",
    summary: "管理、运营整个项目",
    description: "负责整个项目管理运营的人，工作范围包含故事制作、试玩验收、内容发布和运行管理。",
    duties: ["维护故事背景、人物、秘密、目标和故事条件。", "组织试玩、核查问题，管理内容发布并跟进玩家反馈。"],
    authority: ["安排项目的内容与运营工作；具体操作按后台账号权限执行。", "故事资料调整、程序更新和内容版本发布，需要分别核对结果。"],
    collaboration: ["为游戏提供故事资料和体验目标，与玩家反馈、试玩记录相互核对。", "与开发一起明确导演、角色和公共程序的边界，避免遇到问题就随意追加规则。"],
    evidence: ["建议核对：本局使用的故事版本、人物设定、知识范围和初始条件。", "若资料配置与预期不同，先确认资料来源；不能直接归因于AI表现。"],
    questions: ["故事资料的必填内容、验收要求，以及管理与发布权限如何细分？"],
  },
  {
    id: "player",
    name: "玩家",
    summary: "选择身份，参与故事",
    description: "下载安装APP并使用的人。进入一局故事时，选择一个角色，以这个身份参与故事。",
    duties: ["通过角色的视角理解现场，表达想说的话、想做的事。", "根据现场信息作出选择，体验选择带来的变化。"],
    authority: ["决定自己所扮演角色的表达与行动意图。", "行动意图不等于行动已经成功；结果如何确认，需要遵循后续确定的共同规则。"],
    collaboration: ["通过APP提交输入，由公共程序衔接导演与AI角色的回应。", "根据收到的现场变化和角色回应，继续自己的选择。"],
    evidence: ["建议核对：玩家原始输入、所选角色，以及当时实际看到的信息。", "区分提问、猜测、要求与已发生的行动，检查系统是否正确理解输入。"],
    questions: ["自由输入支持哪些行为？遇到无法处理的行动或运行中断时，怎样向玩家说明？"],
  },
  {
    id: "director",
    name: "AI导演",
    summary: "演绎情节，推进故事",
    description: "负责整个故事线情节的演绎和推进。具体导演规则、决定权和干预范围，还需要进一步商量。",
    duties: ["结合故事资料、当前现场和玩家输入，组织事件发展与情节节奏。", "让人物互动与已经发生的事情衔接起来，形成后续情境。"],
    authority: ["情节推进是导演的职责；它能推动哪些事件、改变哪些状态，仍待讨论。", "导演能否要求AI角色采取某个行动、角色能否拒绝，以及冲突如何处理，仍待讨论。"],
    collaboration: ["从公共程序获得本轮所需资料，结合玩家输入组织故事推进。", "与AI角色交换哪些信息、先后如何执行、谁确认行动结果，仍待讨论。"],
    evidence: ["建议核对：导演收到的背景、现场与玩家原话，以及它实际输出的计划。", "先区分输入缺失、计划不合理和程序误拦；错误出现在哪一步，不等于原因就在那一步。"],
    questions: ["导演可以创造哪些事件？怎样保持故事边界内的自主推进？", "导演、玩家、AI角色和程序之间，具体决定权如何划分？"],
  },
  {
    id: "character",
    name: "AI角色",
    summary: "扮演本局的机器人角色",
    description: "故事中未被玩家选择扮演、由AI演绎的角色。例如《美女与野兽》中，玩家选择美女时，野兽就是AI角色。",
    duties: ["按照人物设定和可知信息参与故事，表现角色的语言、态度和反应。", "回应玩家和现场变化，保持人物表现的连贯性。"],
    authority: ["角色的表达需要符合自身身份和可知信息范围。", "自主行动、拒绝、隐瞒和欺骗的允许范围，以及与导演意见不同时如何处理，仍待讨论。"],
    collaboration: ["接收属于自己的角色资料和现场信息，与玩家及其他角色互动。", "角色提出的行动如何形成实际结果，由导演与公共程序按后续约定衔接。"],
    evidence: ["建议核对：角色实际收到的资料、导演给它的要求，以及角色原始输出。", "检查是资料传错、导演要求越界，还是角色输出偏离；不能只看最后一句台词判断责任。"],
    questions: ["AI角色在多大范围内自主作出决定？如何区分角色有意说谎与模型编造？"],
  },
  {
    id: "program",
    name: "公共程序",
    summary: "所有故事共用的运行系统",
    description: "让APP、后台、故事资料和AI配合运行的共用程序。目标是让已有能力范围内的新故事通过资料配置运行。",
    duties: ["管理玩家身份、故事版本、角色信息权限，并组织AI调用。", "记录本局现场、已发生事件和游玩进度，将确认后的结果保存并返回APP。"],
    authority: ["执行已经明确的身份、权限和运行规则，保证结果保存一致。", "哪些条件由程序检查、哪些含义由AI判断、谁最终确认行动结果，仍待讨论。"],
    collaboration: ["读取创作者提供的资料，把玩家输入及相应信息交给导演和AI角色。", "按约定衔接各环节；不同故事复用公共能力，以故事资料表达人物、地点和条件差异。"],
    evidence: ["建议核对：实际传递的信息、检查依据、调用结果，以及保存前后的状态。", "确认是否存在信息分配错误、正常表达被误拦、重复执行或结果保存不一致。"],
    questions: ["公共程序与AI各自负责哪些检查？哪些证据必须保留，才能查清一次失败？"],
  },
];

export function ResponsibilityGuidePage(): ReactNode {
  const sections = [
    { title: "负责什么", key: "duties" },
    { title: "决定权与边界", key: "authority" },
    { title: "如何配合", key: "collaboration" },
    { title: "出问题时查什么", key: "evidence" },
    { title: "待讨论", key: "questions" },
  ] as const;

  return (
    <div className="responsibility-guide">
      <div className="page-heading">
        <div>
          <p className="eyebrow">项目说明</p>
          <h1>职责说明</h1>
        </div>
        <span className="responsibility-draft">职责草案 · 2026-09-29</span>
      </div>

      <div className="responsibility-table-wrap" role="region" aria-label="五部分职责对照表，可滚动查看" tabIndex={0}>
        <table className="responsibility-table">
          <caption className="sr-only">创作者、玩家、AI导演、AI角色与公共程序的职责对照草案</caption>
          <colgroup>
            <col className="responsibility-label-column" />
            {RESPONSIBILITIES.map((item) => <col key={item.id} />)}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">说明项目</th>
              {RESPONSIBILITIES.map((item) => (
                <th scope="col" key={item.id}>
                  <strong>{item.name}</strong>
                  <span>{item.summary}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">身份说明</th>
              {RESPONSIBILITIES.map((item) => <td key={item.id}><p>{item.description}</p></td>)}
            </tr>
            {sections.map((section) => (
              <tr key={section.key} className={section.key === "questions" ? "responsibility-pending-row" : undefined}>
                <th scope="row">{section.title}</th>
                {RESPONSIBILITIES.map((item) => (
                  <td key={item.id}>
                    <ul>{item[section.key].map((text) => <li key={text}>{text}</li>)}</ul>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <aside className="responsibility-evidence-note">
        <strong>排查问题的共同原则</strong>
        <p>核对原始输入 → 核对各环节输出 → 对照约定规则 → 确认原因。证据不足时，先记为“原因待查”。</p>
        <small>以上是排查指引，不代表后台已经具备完整证据或自动归因能力。</small>
      </aside>
    </div>
  );
}
